import { z } from '@fiverr-private/forms';
import { CURRENCY_STYLES, currencyFormat } from '@fiverr-private/localization';
import { appTranslate } from '../../i18n';
import { BillingCycle, PricingDetails, SubscriptionTier } from '../../../type';

interface CreateSchemaProps {
    minQuantity: number;
    maxQuantity: number;
    totalQuantity: number;
}

export const createSchema = ({ minQuantity, maxQuantity, totalQuantity }: CreateSchemaProps) =>
    z.object({
        quantity: z.coerce.number().superRefine((value, ctx) => {
            if (maxQuantity === 0) {
                return;
            }

            if (value < minQuantity) {
                ctx.addIssue({
                    code: z.ZodIssueCode.too_small,
                    minimum: minQuantity,
                    type: 'number',
                    inclusive: true,
                    message: appTranslate<string>('subscription_drawer.fields.quantity.errors.minimum', {
                        params: { min: minQuantity },
                    }),
                });
            }

            if (value > maxQuantity) {
                if (maxQuantity !== totalQuantity) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: appTranslate<string>('subscription_drawer.fields.quantity.errors.maximum_exceed', {
                            params: { maxUsers: totalQuantity, differenceLicenses: maxQuantity },
                        }),
                    });
                } else {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: appTranslate<string>('subscription_drawer.fields.quantity.errors.maximum', {
                            params: { max: totalQuantity },
                        }),
                    });
                }
            }
        }),
        billingCycle: z.enum(Object.values(BillingCycle) as [BillingCycle, ...BillingCycle[]]),
        subscriptionTier: z.enum(Object.values(SubscriptionTier) as [SubscriptionTier, ...SubscriptionTier[]]),
    });

export type FormSchema = z.infer<ReturnType<typeof createSchema>>;

type GetSubscriptionOptionsResult = {
    value: BillingCycle;
    label: string;
    price: string;
}[];

export const getSubscriptionOptions = (subscriptionDetails: Map<BillingCycle, PricingDetails>) => {
    const cycleOptions = [
        {
            billingCycle: BillingCycle.YEARLY,
            value: 'yearly' as const,
        },
        {
            billingCycle: BillingCycle.MONTHLY,
            value: 'monthly' as const,
        },
    ];

    const translateOption = (options: (typeof cycleOptions)[number]) => {
        const price = subscriptionDetails.get(options.billingCycle)?.price;

        if (!price) return null;

        return {
            value: options.billingCycle,
            label: appTranslate(
                `subscription_drawer.fields.billing_cycle.options.${options.value}.label_options.label_option_1`
            ),
            price: appTranslate(
                `subscription_drawer.fields.billing_cycle.options.${options.value}.price_options.price_option_1`,
                {
                    params: {
                        price: currencyFormat({
                            value: price.amountYearly,
                            currencyCode: price.currency,
                            options: {
                                maximumFractionDigits: 2,
                                style: CURRENCY_STYLES.SYMBOL,
                            },
                        }),
                        pricePerMonth: currencyFormat({
                            value: price.amountMonthly,
                            currencyCode: price.currency,
                            options: {
                                maximumFractionDigits: 2,
                                style: CURRENCY_STYLES.SYMBOL,
                            },
                        }),
                    },
                }
            ),
        };
    };

    return cycleOptions.map(translateOption).filter(Boolean) as GetSubscriptionOptionsResult;
};
